import {
  keepPreviousData,
  queryOptions,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query'

import { livePolling } from '@/app/query-client'
import { moversKeys } from '@/features/movers/api/keys'
import { moversService } from '@/features/movers/api/moversService'
import { useMarketStatus } from '@/hooks/useMarketStatus'
import type { MoverBrokerRow, MoverRow, MoversQuery } from '@/schemas/movers'
import type { TablePage, TableQuery } from '@/schemas/table'

/**
 * The Market · Movers table — `/market/movers`'s MAIN data, keyed on the
 * whole search query (view, sector, sort, page, pageSize). The loader awaits
 * it for the ENTRY URL's query; the factory carries the key and the fetch
 * only.
 *
 * The hook takes the whole query rather than five arguments because that is
 * what the screen has — the search params are already a validated
 * `MoversQuery`, and passing the object through means a new filter never has
 * to be threaded past three call sites.
 */
export const moversTableQueryOptions = (query: MoversQuery) =>
  queryOptions<TablePage<MoverRow>>({
    queryKey: moversKeys.table(query),
    queryFn: () => moversService.table(query),
  })

/**
 * Polling is gated on market status exactly as `useIndexSummary` is: nothing
 * in this table can move while NEPSE is shut, so an overnight tab issues no
 * requests at all.
 */
export const useMovers = (query: MoversQuery): UseQueryResult<TablePage<MoverRow>> => {
  const { isOpen } = useMarketStatus()

  return useQuery<TablePage<MoverRow>>({
    ...moversTableQueryOptions(query),
    // A pager click must not blank the table or collapse the pager to "1"
    // while the next page loads; the previous page stays until it arrives.
    placeholderData: keepPreviousData,
    refetchInterval: livePolling(isOpen),
  })
}

/**
 * The Brokers tab's table — the same shape as the movers table, from its own feed (see
 * `moversService.brokers`), keyed on the page and sort. Polled and paged like the table beside it.
 */
export const moversBrokersQueryOptions = (query: TableQuery) =>
  queryOptions<TablePage<MoverBrokerRow>>({
    queryKey: moversKeys.brokers(query),
    queryFn: ({ client }) => moversService.brokers(query, client),
  })

export const useMoversBrokers = (query: TableQuery): UseQueryResult<TablePage<MoverBrokerRow>> => {
  const { isOpen } = useMarketStatus()

  return useQuery<TablePage<MoverBrokerRow>>({
    ...moversBrokersQueryOptions(query),
    placeholderData: keepPreviousData,
    refetchInterval: livePolling(isOpen),
  })
}
